import React, { useEffect, useMemo, useRef, useState } from "react";
import { useStandardProduct } from "app/hooks/shared/standard-product/useStandardProduct";
import StandardProductOptionsHeader from "entities/configurator/standard-product-header";
import {
  CategoryStandardProduct,
  StandardProductCategory,
} from "shared/api/middleware-api-client";
import Icon, { IconTypes } from "shared/ui/icon";
import OptionTemplate from "shared/ui/option-template";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { USPMetaDataType } from "shared/utils/usps";
import { OptionModalState } from "widgets/configurator/option-modal";
import { StandardProductsProductList } from "./StandardProductsProductList";
import { PANEL_CATEGORY_CODE } from "shared/utils/standard-product";
import StandardProductAmountCalculator from "widgets/configurator/standard-product-amount-calculator";
import { SampleSection } from "./SampleSection";
import { MontageServiceSection } from "./MontageServiceSection";
import WhatsappWidget from "widgets/shared/whatsapp-widget";
import { useProductType } from "app/hooks/shared/standard-product/useProductType";
import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";

interface Props {
  setOptionModalState: (state: OptionModalState) => void;
  additionalData: USPMetaDataType;
  selectedCategory: StandardProductCategory;
}
export const AcousticPanel = ({
  setOptionModalState,
  additionalData,
  selectedCategory,
}: Props) => {
  const { store, actions } = useStandardProduct();
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const collectionDropdownRef = useRef<HTMLDivElement>(null);
  const collectionTriggerRef = useRef<HTMLButtonElement>(null);
  const { onStandardProductConfig } = useAnalytics();

  const selectedProduct = useMemo(
    () => store.selectedStandardProduct,
    [store.selectedStandardProduct],
  );

  const type = useProductType(selectedProduct);
  const hideCollectionSection =
    selectedCategory.code === "accessoireakoestischpaneel";

  useEffect(() => {
    if (!isCategoryDropdownOpen) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!collectionDropdownRef.current?.contains(target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      setIsCategoryDropdownOpen(false);
      collectionTriggerRef.current?.focus();
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    document.addEventListener("keydown", handleDocumentKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [isCategoryDropdownOpen]);

  const categories = useMemo(() => {
    return store.standardProductCategories
      .map((categoryProduct) => {
        return {
          ...categoryProduct,
          product: categoryProduct.product.filter((product) => {
            return (
              product.standardProduct.code !== "" &&
              store.selectedStandardProduct &&
              product.standardProduct.disabled !== true &&
              (store.selectedStandardProduct.productType.code.includes(
                "kleurstaal",
              )
                ? product.standardProduct.productType.code.includes(
                    "kleurstaal",
                  )
                : product.standardProduct.productType.code ===
                  "akoestischepanelen")
            );
          }),
        };
      })
      .filter((categoryProduct) => categoryProduct.product.length > 0)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [store.standardProductCategories, store.selectedStandardProduct]);

  const dropdownCategories = useMemo(
    () => categories.filter((category) => category.id !== selectedCategory.id),
    [categories, selectedCategory.id],
  );

  const selectCategory = (category: StandardProductCategory) => {
    actions.selectStandardProductCategory(category);
    if (category.product.length) {
      selectProduct(category.product[0].standardProduct);
    }
    setIsCategoryDropdownOpen(false);
  };

  const selectProduct = (product: CategoryStandardProduct) => {
    actions.selectStandardProduct({
      ...product,
      amount: 1,
    });
    onStandardProductConfig({ ...product, amount: 1 });
  };

  const setProductAmount = (amount: number) => {
    if (!selectedProduct) return;

    // In the panelcalculator de result should be divided by 2.
    // So if you have 10 panels.The amount in the footer should be the same(10 panels).
    // If the result is uneven number round up, so if the result is 1 its 2 panels(one set)
    const finalAmount = type.isSet ? Math.ceil(amount / 2) : amount;
    actions.selectStandardProduct({
      ...selectedProduct,
      amount: finalAmount,
    });

    onStandardProductConfig({ ...selectedProduct, amount: finalAmount });
  };

  return (
    <>
      <StandardProductOptionsHeader
        title={selectedCategory?.name ?? ""}
        code={selectedCategory?.code}
        description={selectedCategory?.description ?? ""}
        selectedProductCode={selectedProduct?.code}
      />

      <WhatsappWidget />

      {!hideCollectionSection && (
        <OptionTemplate
          title="Collectie"
          testId="standard-product-collection-option"
          onShowInfoClick={() =>
            setOptionModalState({
              isOpen: true,
              heading: "Collectie",
              description:
                "Iedere collectie staat voor een ander materiaal of afwerking. Zo kun je bijvoorbeeld " +
                "kiezen voor akoestische panelen met echt houtfineer of luxe behangsoorten zoals suède, metallic en " +
                "extra mat.",
            })
          }
        >
          <div
            ref={collectionDropdownRef}
            className="gg-relative gg-max-w-md gg-pt-4"
          >
            <button
              ref={collectionTriggerRef}
              type="button"
              aria-expanded={isCategoryDropdownOpen}
              aria-controls="standard-product-collection-dropdown"
              className="gg-flex gg-h-11 gg-w-full gg-cursor-pointer gg-items-center gg-justify-between gg-rounded-sm gg-border gg-border-dark-blue-200 gg-px-3 gg-py-1.5 focus:gg-outline-none focus-visible:gg-ring-2 focus-visible:gg-ring-dark-blue"
              onClick={() => setIsCategoryDropdownOpen((current) => !current)}
            >
              <div className="gg-flex gg-w-full gg-items-center gg-justify-between">
                <Text className="gg-flex gg-items-center">
                  {selectedCategory ? selectedCategory.name : "Choose category"}
                </Text>

                <Icon
                  name={IconTypes.ARROW_LEFT}
                  className={concatClassnames(
                    isCategoryDropdownOpen ? "gg-rotate-90" : "-gg-rotate-90",
                    "gg-transition-all",
                  )}
                />
              </div>
            </button>

            {isCategoryDropdownOpen && (
              <ul
                id="standard-product-collection-dropdown"
                aria-label="Beschikbare collecties"
                className="gg-absolute gg-left-0 gg-right-0 gg-top-full gg-z-30 gg-max-h-64 gg-divide-y gg-divide-dark-blue-200 gg-overflow-y-auto gg-rounded-b-sm gg-border gg-border-t-0 gg-border-dark-blue-200 gg-bg-white gg-shadow-lg"
              >
                {dropdownCategories.map((category) => (
                  <li key={category.id} className="gg-w-full">
                    <button
                      type="button"
                      className="gg-min-h-11 gg-flex gg-w-full gg-cursor-pointer gg-items-center gg-px-3 gg-py-1.5 gg-text-left hover:gg-bg-gray-100 focus:gg-outline-none focus-visible:gg-ring-2 focus-visible:gg-ring-dark-blue"
                      onClick={() => selectCategory(category)}
                    >
                      <Text className="gg-text-sm" bold>
                        {category.name}
                      </Text>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </OptionTemplate>
      )}

      <OptionTemplate
        title={additionalData.title}
        testId="standard-product-zen-en-zacht-option"
        onShowInfoClick={() =>
          setOptionModalState({
            isOpen: true,
            heading: additionalData.information_title,
            description: additionalData.information_description,
          })
        }
        className="gg-pb-[84px]"
      >
        <StandardProductsProductList selectedCategory={selectedCategory} />
      </OptionTemplate>

      <SampleSection
        selectedProduct={selectedProduct}
        selectedCategoryCode={selectedCategory.code}
      />

      {selectedProduct &&
        selectedProduct.productType.code === PANEL_CATEGORY_CODE && (
          <OptionTemplate
            title="Hoeveel panelen heb ik nodig?"
            onShowInfoClick={() =>
              setOptionModalState({
                isOpen: true,
                heading: "Bereken het aantal panelen",
                description:
                  "Met behulp van deze handige rekentool, reken je gemakkelijk uit hoe veel panelen je " +
                  "nodig hebt. Vul de breedte en hoogte in van het oppervlak waarop jij de panelen wilt toepassen. " +
                  "Akoestische panelen van GewoonGers hebben een afmeting van 60 bij 268 centimeter (bxh).",
              })
            }
          >
            <StandardProductAmountCalculator
              setStandardProductAmount={setProductAmount}
            />
          </OptionTemplate>
        )}

      <MontageServiceSection />
    </>
  );
};
